/**
 * AssistantLayout.jsx
 * src/layouts/AssistantLayout.jsx
 *
 * Layout wrapper for the Assistant panel.
 * Mirrors AdminLayout.jsx structure exactly.
 *
 * Setup:
 *   npm install bootstrap
 *   In main.jsx → import 'bootstrap/dist/css/bootstrap.min.css'
 *
 * Usage in App.jsx:
 *   import AssistantLayout from './layouts/AssistantLayout';
 *
 *   <Route path="/assistant" element={<AssistantLayout />}>
 *     <Route index                    element={<AssistantHome />} />
 *     <Route path="schedule"          element={<TodaysSchedule />} />
 *     <Route path="appointments"      element={<AllAppointments />} />
 *     <Route path="patients"          element={<PatientsList />} />
 *     <Route path="checkin"           element={<PatientCheckin />} />
 *     <Route path="followup"          element={<PatientFollowup />} />
 *     <Route path="inventory"         element={<Inventory />} />
 *     <Route path="billing"           element={<BillingAssist />} />
 *     <Route path="doctor-schedule"   element={<DoctorSchedule />} />
 *     <Route path="messages"          element={<Messages />} />
 *     <Route path="reminders"         element={<Reminders />} />
 *     <Route path="profile"           element={<AssistantProfile />} />
 *     <Route path="settings"          element={<AssistantSettings />} />
 *   </Route>
 */

import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";

/* ══════════════════════════════════════════════════
   ROUTE MAP  — menu id → URL path
   Must match the item ids defined in Sidebar MENUS.assistant
══════════════════════════════════════════════════ */
const ROUTES = {
  asst_dashboard:   "/assistant",
  todays_schedule:  "/assistant/schedule",
  all_appointments: "/assistant/appointments",
  patients_list:    "/assistant/patients",
  patient_checkin:  "/assistant/checkin",
  patient_followup: "/assistant/followup",
  inventory:        "/assistant/inventory",
  billing_assist:   "/assistant/billing",
  doctor_schedule:  "/assistant/doctor-schedule",
  messages:         "/assistant/messages",
  reminders:        "/assistant/reminders",
  asst_profile:     "/assistant/profile",
  asst_settings:    "/assistant/settings",
  create_appointment: "/assistant/create-appointment",
};

/* Reverse map: URL path → menu id (for active highlight) */
const PATH_TO_ID = Object.fromEntries(
  Object.entries(ROUTES).map(([id, path]) => [path, id])
);

/* ══════════════════════════════════════════════════
   GET LOGGED-IN USER FROM LOCALSTORAGE
══════════════════════════════════════════════════ */
function getAuthUser() {
  try {
    const stored = localStorage.getItem("user");
    if (!stored) return { name: "Assistant", email: "" };
    const parsed = JSON.parse(stored);
    if (typeof parsed === "string") return { name: parsed, email: "" };
    return {
      name:  parsed.name  || parsed.Name  || "Assistant",
      email: parsed.email || parsed.Email || "",
    };
  } catch {
    return { name: "Assistant", email: "" };
  }
}

/* ══════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════ */
export default function AssistantLayout() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const authUser   = getAuthUser();
  const activeMenu = PATH_TO_ID[location.pathname] || "asst_dashboard";

  const handleMenuChange = (id) => {
    const route = ROUTES[id];
    if (route) navigate(route);
    setSidebarOpen(false); // close sidebar on mobile after click
  };

  return (
    <>
      <style>{`
        body {
          background: #EEF2F7;
          font-family: 'Nunito', 'Segoe UI', sans-serif;
        }

        /* ── Sidebar ── */
        .asst-sidebar {
          position: fixed; top: 0; left: 0;
          width: 240px; height: 100vh;
          overflow-y: auto; overflow-x: hidden;
          z-index: 1040;
          transition: transform .25s ease;
        }

        /* ── Main content ── */
        .asst-main {
          margin-left: 240px;
          min-height: 100vh;
          overflow-y: auto;
        }

        /* ── Mobile overlay ── */
        .asst-overlay {
          display: none;
          position: fixed; inset: 0;
          background: rgba(0, 0, 0, 0.45);
          z-index: 1039;
        }

        /* ── Responsive breakpoint ── */
        @media (max-width: 991px) {
          .asst-main         { margin-left: 0; }
          .asst-sidebar      { transform: translateX(-100%); }
          .asst-sidebar.open { transform: translateX(0); }
          .asst-overlay.open { display: block; }
        }
      `}</style>

      {/* ── Mobile overlay (closes sidebar when tapped) ── */}
      <div
        className={`asst-overlay ${sidebarOpen ? "open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ── Sidebar ── */}
      <div className={`asst-sidebar ${sidebarOpen ? "open" : ""}`}>
        <Sidebar
          role="assistant"
          user={authUser}
          activeMenu={activeMenu}
          onMenuChange={handleMenuChange}
        />
      </div>

      {/* ── Main content ── */}
      <div className="asst-main">

        {/* Mobile topbar — visible only below lg breakpoint */}
        <nav
          className="d-lg-none d-flex align-items-center px-3 py-2"
          style={{
            background:   "#0A1628",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            position:     "sticky",
            top:          0,
            zIndex:       100,
          }}
        >
          {/* Hamburger */}
          <button
            className="btn btn-sm me-3"
            style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", fontSize: 16 }}
            onClick={() => setSidebarOpen(true)}
          >
            ☰
          </button>

          {/* Clinic name */}
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
            BrightSmile Dental
          </span>

          {/* Role pill on the right */}
          <span
            className="ms-auto badge"
            style={{
              background: "rgba(216,90,48,0.2)",
              color:      "#F0997B",
              border:     "1px solid rgba(216,90,48,0.35)",
              fontSize:   10,
              fontWeight: 700,
            }}
          >
            🤝 Assistant
          </span>
        </nav>

        {/* Page content injected here by React Router */}
        <Outlet />
      </div>
    </>
  );
}